import * as mapboxgl from 'mapbox-gl';
import React from 'react';

import {PropertyColorRule} from 'app/modules/Remote/FeatureCollection';

const ZOOM_THRESHOLD = 15;

export interface PolygonLayerOpts {
  idPrefix: string;
  sourceId: string;
  color: string;
  sourceLayer?: string;
  fillPaintExtra?: mapboxgl.FillPaint;
  linePaintExtra?: mapboxgl.LinePaint;
  // Show this layer as filled by default regardless of zoom level + altDown state.
  defaultFilled?: boolean;
  // Don't show this layer as filled by default regardless of zoom level.
  // This is useful for overlays shown over medium to low resolution imagery, where you'd
  // want to review the entire property while zoomed out (without the overlay in the way).
  defaultUnfilled?: boolean;
  // percentage decimal value from 0 and 1
  fillOpacityOverride?: number;
  // percentage decimal value from 0 and 1, only applied on mouseenter of a specific feature (via feature state)
  hoverFillFeatureOpacity?: number;
  propertyColorRule?: PropertyColorRule | null;
}

export function buildColorExpression(
  color: string,
  propertyColorRule?: PropertyColorRule | null
): string | mapboxgl.ExpressionSpecification {
  if (
    !propertyColorRule ||
    !propertyColorRule.propertyKey ||
    Object.keys(propertyColorRule.colors).length === 0
  ) {
    return color;
  }
  const matchArgs: (string | number)[] = [];
  for (const [value, valueColor] of Object.entries(propertyColorRule.colors)) {
    matchArgs.push(value, valueColor);
  }
  return [
    'match',
    ['to-string', ['get', propertyColorRule.propertyKey]],
    ...matchArgs,
    color,
  ] as unknown as mapboxgl.ExpressionSpecification;
}

/**
 * Returns an array of two layers, for polygon fill and polygon lines,
 * respectively. The fill layer is semi-transparent below the ZOOM_THRESHOLD
 * and transparent above the ZOOM_THRESHOLD so as not to obscure the map while
 * still allowing mouse events to fire.
 *
 * If altDown is true, the polygon will be filled regardless of zoom level.
 * If opts.defaultFilled is true then altDown has the opposite behavior
 * (not drawing the fill regardless of zoom level).
 *
 * Slight misnomer now that this handles line strings and points.
 */
export function makeTranslucentPolygonLayer(opts: PolygonLayerOpts, altDown = false) {
  let fillZoomThreshold: number;
  if (opts.defaultUnfilled) {
    fillZoomThreshold = altDown ? 24 : 1;
  } else {
    const forceFill = opts.defaultFilled ? !altDown : altDown;
    fillZoomThreshold = forceFill ? 24 : ZOOM_THRESHOLD;
  }

  const colorExpression = buildColorExpression(opts.color, opts.propertyColorRule);

  // Rule-based overlays often stack overlapping features (e.g., year-extent
  // datasets), where the default 0.6 fill gets visually saturated. Drop to
  // 0.25 just for those; single-color overlays stay at 0.6 so they don't get
  // washed out.
  const defaultFillOpacity = opts.propertyColorRule ? 0.25 : 0.6;

  const fillLayer: mapboxgl.FillLayerSpecification = {
    type: 'fill',

    id: `${opts.idPrefix}-fill`,
    source: opts.sourceId,
    ...(opts.sourceLayer ? {'source-layer': opts.sourceLayer} : {}),

    filter: [
      'all',
      // We have to explicitly disable fill for lines or else Mapbox will try,
      // and it will be a mess.
      ['!=', ['geometry-type'], 'LineString'],
      ['!=', ['geometry-type'], 'MultiLineString'],
    ],
    paint: {
      'fill-color': colorExpression,
      'fill-opacity': [
        'step', // Semi-transparent below the zoom threshold, fully transparent above it.
        ['zoom'],
        opts.hoverFillFeatureOpacity !== undefined
          ? [
              'case',
              ['boolean', ['feature-state', 'hovered'], false],
              opts.hoverFillFeatureOpacity,
              opts.fillOpacityOverride ?? defaultFillOpacity,
            ]
          : (opts.fillOpacityOverride ?? defaultFillOpacity),
        fillZoomThreshold,
        0,
      ],
      ...(opts.fillPaintExtra || {}),
    },
  };

  const linesLayer: mapboxgl.LineLayerSpecification = {
    type: 'line',

    id: `${opts.idPrefix}-lines`,
    source: opts.sourceId,
    ...(opts.sourceLayer ? {'source-layer': opts.sourceLayer} : {}),

    paint: {
      'line-color': colorExpression,
      'line-opacity': 1.0,
      'line-width': ['step', ['zoom'], 1.5, fillZoomThreshold, 3],
      ...(opts.linePaintExtra || {}),
    },
  };

  const pointLayer: mapboxgl.CircleLayerSpecification = {
    type: 'circle',

    id: `${opts.idPrefix}-points`,
    source: opts.sourceId,
    ...(opts.sourceLayer ? {'source-layer': opts.sourceLayer} : {}),

    filter: ['any', ['==', ['geometry-type'], 'Point'], ['==', ['geometry-type'], 'MultiPoint']],

    paint: {
      'circle-color': colorExpression,
      // We have to change size with zoom to make the points look natural /
      // visible.
      'circle-radius': ['step', ['zoom'], 3, 13, 4, 16, 6],
      'circle-opacity': 0.8,
    },
  };

  return [fillLayer, linesLayer, pointLayer] as const;
}

/**
 * Hook wrapper around makeTranslucentPolygonLayer that listens for key events
 * and forces the layers to be filled when alt is held down.
 *
 * (We chose alt because mapboxgl does not call the map’s click handler when
 * shift is held down.)
 */
export function useFilledPolygonLayer(
  opts: PolygonLayerOpts,
  popup?: mapboxgl.Popup
): readonly [mapboxgl.FillLayer, mapboxgl.LineLayer, mapboxgl.CircleLayer] {
  const [isAltDown, setIsAltDown] = React.useState(false);

  React.useEffect(() => {
    const keyDownHandler = (ev: KeyboardEvent) => {
      const tagName = (ev.target as HTMLElement).tagName;

      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        // Keeps us from flashing when the user types fancy characters.
        return;
      }

      if (ev.keyCode === 18) {
        setIsAltDown(true);
      }
    };

    const keyUpHandler = (ev: KeyboardEvent) => {
      const tagName = (ev.target as HTMLElement).tagName;

      if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
        // Keeps us from flashing when the user types fancy characters.
        return;
      }

      if (ev.keyCode === 18) {
        setIsAltDown(false);
      }
    };

    document.addEventListener('keydown', keyDownHandler);
    document.addEventListener('keyup', keyUpHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
      document.removeEventListener('keyup', keyUpHandler);
    };
  });

  // This closes the popup when alt is lifted to reduce the chances that the
  // popup is dangling around, since mouse leave wouldn’t fire.
  React.useEffect(() => {
    if (popup && isAltDown) {
      return () => {
        popup.remove();
      };
    }
  }, [popup, isAltDown]);

  return React.useMemo(() => makeTranslucentPolygonLayer(opts, isAltDown), [opts, isAltDown]);
}
